import React, { useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircleOutline, DeleteOutline } from '@mui/icons-material';
import { toast } from 'react-toastify';
import PostalCodeInput from 'components/masked-input/PostalCodeInput';
import { AddressErrors, emptyAddress } from 'helpers/addressValidation';
import { postalCodeSearch } from 'services/postalCodeSearch';
import { Address } from 'types/address';
import { asInputComponent } from 'components/masked-input/asInputComponent';

const STATES = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

const NO_NUMBER = 'S/N';

// genérico para manter campos extras do endereço de cada cadastro (doctor_id, hospital_id...)
interface AddressFieldsProps<T extends Address> {
  address: T | null;
  onChange(address: T): void;
  errors?: AddressErrors;
  // quando informado e o endereço já existe, mostra "Limpar endereço"
  onClear?(): void;
  clearing?: boolean;
}

function AddressFields<T extends Address>({
  address,
  onChange,
  errors = {},
  onClear,
  clearing = false,
}: AddressFieldsProps<T>) {
  const value = (address ?? emptyAddress) as T;
  const latest = useRef(value);
  latest.current = value;
  const numberRef = useRef<HTMLInputElement>(null);
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState(false);
  const [notFound, setNotFound] = useState(false);

  function set(field: keyof Address, fieldValue: string) {
    onChange({ ...latest.current, [field]: fieldValue });
  }

  // preenche rua, bairro, cidade e UF e mantém número e complemento já digitados
  function search(zipCode: string) {
    setSearching(true);

    postalCodeSearch(zipCode.replace(/\D/g, ''))
      .then(response => {
        const { data } = response;
        if (data.erro) {
          setNotFound(true);
          return;
        }

        onChange({
          ...latest.current,
          zip_code: zipCode,
          street: data.logradouro,
          neighborhood: data.bairro,
          city: data.localidade,
          state: data.uf,
        });
        setFound(true);
        numberRef.current?.focus();
      })
      .catch(() => toast.error('Não foi possível buscar o endereço pelo CEP'))
      .finally(() => setSearching(false));
  }

  function handleZipCode(zipCode: string) {
    // o campo mascarado avisa o valor inicial ao montar; só conta o que o usuário mudou
    if (zipCode === latest.current.zip_code) return;

    setFound(false);
    setNotFound(false);
    set('zip_code', zipCode);

    if (zipCode.replace(/\D/g, '').length === 8) search(zipCode);
  }

  const zipHelper = notFound ? 'CEP não encontrado. Confira os números.' : errors.zip_code;
  const noNumber = value.street_number === NO_NUMBER;
  const states = value.state && !STATES.includes(value.state) ? [value.state, ...STATES] : STATES;

  return (
    <Grid container size={12} spacing={2}>
      <Grid size={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <div>
          <Typography variant="subtitle1" fontWeight={500}>
            Endereço
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Digite o CEP e o restante é preenchido sozinho.
          </Typography>
        </div>

        {onClear && !!address?.id && (
          <Button
            color="error"
            size="small"
            startIcon={<DeleteOutline />}
            disabled={clearing}
            onClick={onClear}
            sx={{ flexShrink: 0 }}
          >
            Limpar endereço
          </Button>
        )}
      </Grid>

      <Grid size={{ md: 3, xs: 12 }}>
        <TextField
          fullWidth
          autoFocus={!address?.zip_code}
          label="CEP"
          value={value.zip_code}
          onChange={e => handleZipCode(e.target.value)}
          error={!!zipHelper}
          helperText={zipHelper}
          slotProps={{
            input: {
              inputComponent: asInputComponent(PostalCodeInput),
              endAdornment: (
                <InputAdornment position="end">
                  {searching ? (
                    <CircularProgress size={20} />
                  ) : found ? (
                    <CheckCircleOutline fontSize="small" color="success" />
                  ) : null}
                </InputAdornment>
              ),
            },
          }}
        />
      </Grid>

      <Grid size={{ md: 6, xs: 12 }}>
        <TextField
          fullWidth
          label="Rua"
          value={value.street}
          onChange={e => set('street', e.target.value)}
          error={!!errors.street}
          helperText={errors.street}
        />
      </Grid>

      <Grid size={{ md: 3, xs: 12 }}>
        <TextField
          fullWidth
          label="Número"
          inputRef={numberRef}
          value={value.street_number}
          disabled={noNumber}
          onChange={e => set('street_number', e.target.value)}
          error={!!errors.street_number}
          helperText={errors.street_number}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={noNumber}
              onChange={e => set('street_number', e.target.checked ? NO_NUMBER : '')}
            />
          }
          label={<Typography variant="caption">Sem número</Typography>}
        />
      </Grid>

      <Grid size={{ md: 6, xs: 12 }}>
        <TextField
          fullWidth
          label="Complemento"
          placeholder="Apto, bloco, referência"
          value={value.complement ?? ''}
          onChange={e => set('complement', e.target.value)}
        />
      </Grid>

      <Grid size={{ md: 6, xs: 12 }}>
        <TextField
          fullWidth
          label="Bairro"
          value={value.neighborhood}
          onChange={e => set('neighborhood', e.target.value)}
          error={!!errors.neighborhood}
          helperText={errors.neighborhood}
        />
      </Grid>

      <Grid size={{ md: 9, xs: 8 }}>
        <TextField
          fullWidth
          label="Cidade"
          value={value.city}
          onChange={e => set('city', e.target.value)}
          error={!!errors.city}
          helperText={errors.city}
        />
      </Grid>

      <Grid size={{ md: 3, xs: 4 }}>
        <TextField
          select
          fullWidth
          label="UF"
          value={value.state}
          onChange={e => set('state', e.target.value)}
          error={!!errors.state}
          helperText={errors.state}
        >
          {states.map(state => (
            <MenuItem key={state} value={state}>
              {state}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );
}

export default AddressFields;
